import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import "../styles/nav.scss";
import logo from "../images/dog_wash_transparent.png";
import pawIcon from "../images/icons/map_markers/Paw_Icon_Small.png";

const navItems = [
  { id: "home", to: "/", label: "Home" },
  { id: "demo", to: "/demo", label: "How it works" },
  { id: "pricing", to: "/pricing", label: "Pricing" },
  { id: "faq", to: "/faq", label: "FAQ" },
];

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Nav = ({ page = "" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const hamburgerRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) {
      document.body.style.overflow = "";
      return undefined;
    }

    document.body.style.overflow = "hidden";

    const drawer = drawerRef.current;
    const focusable = drawer
      ? Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR))
      : [];
    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    firstFocusable?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setDrawerOpen(false);
        hamburgerRef.current?.focus();
        return;
      }

      if (event.key !== "Tab" || focusable.length === 0) {
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable?.focus();
        }
        return;
      }

      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [drawerOpen]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    hamburgerRef.current?.focus();
  };

  const drawerVariants = reduceMotion
    ? {}
    : {
        closed: { x: "100%" },
        open: { x: 0 },
      };

  return (
    <>
      <nav
        id="nav"
        className={`nav${scrolled ? " nav--scrolled" : ""}`}
        aria-label="Main navigation"
      >
        <Link to="/" className="nav__logo">
          <img className="nav__logo-img" src={logo} alt="Pawwash logo" />
        </Link>

        <div className="nav__menu nav__menu--desktop">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.to}
              className={`nav__item${
                page === item.id ? " nav__item--active" : ""
              }`}
            >
              <img src={pawIcon} alt="" aria-hidden="true" className="nav__paw" />
              <span>{item.label}</span>
            </Link>
          ))}
        </div>

        <IconButton
          ref={hamburgerRef}
          className="nav__hamburger"
          aria-label={drawerOpen ? "Close menu" : "Open menu"}
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((open) => !open)}
        >
          {drawerOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </nav>

      <AnimatePresence>
        {drawerOpen ? (
          <>
            <motion.button
              type="button"
              className="nav__overlay"
              aria-label="Close menu"
              initial={reduceMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduceMotion ? undefined : { opacity: 0 }}
              onClick={closeDrawer}
            />
            <motion.div
              ref={drawerRef}
              className="nav__drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Main navigation"
              initial={reduceMotion ? false : "closed"}
              animate="open"
              exit={reduceMotion ? undefined : "closed"}
              variants={drawerVariants}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            >
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.to}
                  className={`nav__drawer-item${
                    page === item.id ? " nav__drawer-item--active" : ""
                  }`}
                >
                  <img src={pawIcon} alt="" aria-hidden="true" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default Nav;
